import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { API_CONFIG } from '../../../utils/apiConfig';

const DirectMigration = () => {
  const [migrating, setMigrating] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  const { currentUser } = useAuth();

  const getAuthToken = useCallback(async () => {
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    return await currentUser.getIdToken(true);
  }, [currentUser]);

  const migrateSoldProducts = async () => {
    setMigrating(true);
    setResult(null);
    
    try {
      const token = await getAuthToken();
      
      const response = await axios.post(`${API_CONFIG.BASE_URL}/api/direct-migration/migrate-sold-products`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResult({
        success: true,
        message: 'Sold products migrated successfully!',
        details: response.data.data
      });
      
      // Auto-verify after migration
      await verifySoldProducts();
      
    } catch (error) {
      setResult({
        success: false,
        message: 'Migration failed',
        error: error.response?.data?.message || error.message
      });
    } finally {
      setMigrating(false);
    }
  };

  const verifySoldProducts = useCallback(async () => {
    setVerifying(true);
    
    try {
      const token = await getAuthToken();
      
      console.log('Verifying sold products data...');
      const response = await axios.get(`${API_CONFIG.BASE_URL}/api/direct-migration/verify-sold-products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Verification response:', response.data);
      setVerificationData(response.data.data);
      
    } catch (error) {
      console.error('Verification error:', error);
    } finally {
      setVerifying(false);
    }
  }, [getAuthToken]);

  useEffect(() => {
    verifySoldProducts();
  }, [verifySoldProducts]);

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h2>💾 Direct Sold Products Migration</h2>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        This will save all visible sold products data directly to the SoldProducts collection in the database.
      </p>

      {/* Migration Controls */}
      <div style={{ 
        backgroundColor: '#f8f9fa', 
        padding: '20px', 
        borderRadius: '8px', 
        marginBottom: '20px',
        border: '1px solid #dee2e6'
      }}>
        <h3>Migration Controls</h3>
        
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
          <button
            onClick={migrateSoldProducts}
            disabled={migrating}
            style={{
              backgroundColor: migrating ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '5px',
              cursor: migrating ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            {migrating ? '🔄 Migrating...' : '💾 Migrate Sold Products'}
          </button>
          
          <button
            onClick={verifySoldProducts}
            disabled={verifying}
            style={{
              backgroundColor: verifying ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '5px',
              cursor: verifying ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            {verifying ? '🔄 Verifying...' : '🔍 Verify Data'}
          </button>
        </div>

        {/* Migration Status */}
        {migrating && (
          <div style={{ color: '#007bff', fontWeight: 'bold' }}>
            🔄 Migrating sold products data... Please wait.
          </div>
        )}
        
        {result && (
          <div style={{ 
            backgroundColor: result.success ? '#d4edda' : '#f8d7da', 
            padding: '15px', 
            borderRadius: '5px', 
            marginBottom: '15px',
            border: `1px solid ${result.success ? '#c3e6cb' : '#f5c6cb'}`
          }}>
            <h4 style={{ 
              color: result.success ? '#155724' : '#721c24', 
              margin: '0 0 10px 0' 
            }}>
              {result.success ? '✅ Migration Successful!' : '❌ Migration Failed'}
            </h4>
            <p style={{ 
              color: result.success ? '#155724' : '#721c24',
              margin: '0 0 10px 0'
            }}>
              {result.message}
            </p>
            
            {result.details && (
              <div style={{ fontSize: '14px', color: result.success ? '#155724' : '#721c24' }}>
                <p><strong>Total Migrated:</strong> {result.details.totalMigrated}</p>
                <p><strong>By Source:</strong> Cash: {result.details.summary?.bySource?.cashBills || 0}, Credit: {result.details.summary?.bySource?.creditBills || 0}</p>
                <p><strong>By Product:</strong> Purifier: {result.details.summary?.byProduct?.purifier || 0}, Fridge: {result.details.summary?.byProduct?.fridge || 0}, Laptop: {result.details.summary?.byProduct?.laptop || 0}</p>
                <p><strong>Total Value:</strong> ₹{result.details.summary?.totalValue || 0}</p>
              </div>
            )}
            
            {result.error && (
              <p style={{ color: '#721c24', fontSize: '14px' }}>
                <strong>Error:</strong> {result.error}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Verification Results */}
      {verificationData && (
        <div style={{ 
          backgroundColor: '#e3f2fd', 
          padding: '15px', 
          borderRadius: '5px', 
          marginBottom: '20px',
          border: '1px solid #bbdefb'
        }}>
          <h4 style={{ color: '#1565c0', margin: '0 0 10px 0' }}>📊 Current Sold Products Data</h4>
          <div style={{ fontSize: '14px', color: '#1565c0' }}>
            <p><strong>Total Sold Products:</strong> {verificationData.totalSoldProducts}</p>
            <p><strong>By Source:</strong> Cash: {verificationData.bySource?.cashBills || 0}, Credit: {verificationData.bySource?.creditBills || 0}, Manual: {verificationData.bySource?.manualEntries || 0}</p>
            
            {verificationData.byProduct && Object.keys(verificationData.byProduct).length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <p><strong>By Product:</strong></p>
                {Object.entries(verificationData.byProduct).map(([product, data]) => (
                  <div key={product} style={{ marginLeft: '20px', marginBottom: '5px' }}>
                    <strong>{product}:</strong> {data.count} sales, {data.totalQuantity} units, ₹{data.totalValue} value
                    <span style={{ fontSize: '12px', color: '#666' }}> (Sources: {data.sources.join(', ')})</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sample Data Preview */}
      <div style={{ 
        backgroundColor: '#fff3cd', 
        padding: '15px', 
        borderRadius: '5px',
        border: '1px solid #ffeaa7'
      }}>
        <h4 style={{ color: '#856404', margin: '0 0 10px 0' }}>📋 Data to be Migrated</h4>
        <div style={{ fontSize: '14px', color: '#856404' }}>
          <p><strong>Products to migrate:</strong></p>
          <ul style={{ margin: '10px 0', paddingLeft: '20px' }}>
            <li><strong>Purifier (ITEM5):</strong> 3 sales (2 CashBill, 1 CreditBill) - ₹20,000 each</li>
            <li><strong>Fridge (ITEM2):</strong> 1 sale (CashBill) - ₹45,000</li>
            <li><strong>Laptop (ITEM1):</strong> 1 sale (CreditBill) - ₹80,000</li>
          </ul>
          <p><strong>Total:</strong> 5 sales, 5 units, ₹185,000 value</p>
        </div>
      </div>

      {/* Instructions */}
      <div style={{ 
        backgroundColor: '#d1ecf1', 
        padding: '15px', 
        borderRadius: '5px',
        border: '1px solid #bee5eb'
      }}>
        <h4 style={{ color: '#0c5460', margin: '0 0 10px 0' }}>Instructions</h4>
        <ol style={{ color: '#0c5460', fontSize: '14px', margin: '0' }}>
          <li><strong>Click "Migrate Sold Products"</strong> to save all visible sold products data to the database</li>
          <li><strong>Click "Verify Data"</strong> to check what's currently in the SoldProducts collection</li>
          <li>After migration, the inventory calculations should show correct "Total Sold" values</li>
          <li>Purifier should show 3 sold, Fridge should show 1 sold, Laptop should show 1 sold</li>
        </ol>
      </div>
    </div>
  );
};

export default DirectMigration;
